// === CENTRAL PRODUCT REGISTRY ===
// Single source for per-product wiring metadata (carrier, premium location, RI field
// names, fee participation). Today every product duplicates these across ~12 files;
// this registry centralizes them so each product is defined ONCE.
//
// Introduced as the vehicle for the doorlopendereis (DFD / K781) product. The map is
// Partial<...> on purpose: only products that have been migrated have an entry, so the
// existing products can be folded in one at a time without a big-bang refactor — every
// consumer must already handle a missing entry (getProductEntry can return undefined).
//
// Keep this module dependency-light: it imports only the SaaProductId *type* (erased at
// compile time) so it stays free of React/icon imports and can be used by pure utils
// (policyCost, subscriptionFee) without circular-import risk. Use a string `iconKey`
// rather than an icon component, and keep routes in workflowConfig.ts / Router.tsx.

import type { SaaProductId } from '../utils/addressSharing';
import { computeDoorlopendeReisDisplayPremium } from '../utils/doorlopendeReisPricing';
// Pure constants module (imports nothing) — no circular-import risk. Single source for the
// DR carrier code (K781; the P413 test lives in PP_MYAAND/PP_MYNAAM only — see fields.ts).
import { DOORLOPENDE_REIS_CARRIER } from '../pages/screens/doorlopendereis/fields';
import { ONGEVALLEN_CARRIER } from '../pages/screens/ongevallen/fields';

export type { SaaProductId } from '../utils/addressSharing';

/**
 * Acceptance (RI) field-name map. Most products answer RI_OGB / RI_POLJUS / RI_OVRMED,
 * but doorlopendereis uses a different schema (RI_OGBOOIT / RI_POLJUS8). Keys are the
 * shared question ids; values are the product's actual AFD field names.
 */
export interface RiFieldNames {
  ogb: string; // "ooit geweigerd/opgezegd/beperkt"  — RI_OGB | RI_OGBOOIT
  poljus: string; // strafrechtelijk verleden          — RI_POLJUS | RI_POLJUS8
  ovrmed: string; // overige mededelingen               — RI_OVRMED (same so far)
}

export interface ProductRegistryEntry {
  /** Canonical id — reuses SaaProductId from addressSharing.ts. */
  id: SaaProductId;
  /** Carrier / maatschappij code, e.g. 'K781'. */
  carrierCode: string;
  /** BFF product name, e.g. 'SAA DFD Reisverzekering'. */
  productName: string;
  /** BFF calculate endpoint this product POSTs to. */
  calcEndpoint: string;
  /**
   * i18n key for the display name (summary / payment / breakdown cards). Uses the
   * complete products.<id>.name namespace rather than the incomplete
   * screens.offerSummary.productNames.* block.
   */
  nameKey: string;
  /**
   * Read the display (gross) premium off a product's stored workflow step data.
   * A function (not a path string) so each product encodes its own quirk
   * (DR -> PP.PP_TTOT, AVP -> PP.DA.DA_TTOT, etc.). Returns null when not yet priced.
   */
  premiumAccessor: (stepData: any) => number | null;
  /** Coverage section this product writes into the Contractdocument (DR -> 'DR'). */
  coverageSection: string;
  /** Acceptance field-name map (DR differs from the rest). */
  riFieldNames: RiFieldNames;
  /** Breakdown / summary icon key (mapped to an icon by each surface). */
  iconKey?: string;
  /** Whether this product counts toward policyCost / subscriptionFee tiers. */
  countsTowardFees?: boolean;
  /**
   * Property products that insure a building collect a separate "risicoadres"
   * (the address of the insured property), which may differ from the
   * policyholder address. Opt in here; non-property products leave it falsy.
   */
  requiresRiskAddress?: boolean;
}

export const PRODUCT_REGISTRY: Partial<Record<SaaProductId, ProductRegistryEntry>> = {
  doorlopendereis: {
    id: 'doorlopendereis',
    carrierCode: DOORLOPENDE_REIS_CARRIER,
    productName: 'SAA DFD Reisverzekering',
    calcEndpoint: '/api/v1/productservice/calculate',
    nameKey: 'products.doorlopendereis.name',
    // Ex-provision, tax-exempt display price (see doorlopendeReisPricing.ts).
    premiumAccessor: (stepData) => computeDoorlopendeReisDisplayPremium(stepData?.Contractdocument?.PP),
    coverageSection: 'DR',
    riFieldNames: { ogb: 'RI_OGBOOIT', poljus: 'RI_POLJUS8', ovrmed: 'RI_OVRMED' },
    iconKey: 'calendar',
    countsTowardFees: true,
  },
  ongevallen: {
    id: 'ongevallen',
    carrierCode: ONGEVALLEN_CARRIER,
    productName: 'DFD Ongevallenverzekering',
    calcEndpoint: '/api/v1/productservice/calculate',
    nameKey: 'products.ongevallen.name',
    premiumAccessor: (stepData) => computeDoorlopendeReisDisplayPremium(stepData?.Contractdocument?.PP),
    coverageSection: 'OD',
    riFieldNames: { ogb: 'RI_OGBOOIT', poljus: 'RI_POLJUS8', ovrmed: 'RI_OVRMED' },
    iconKey: 'firstAid',
    countsTowardFees: true,
  },
};

/** Look up a product's registry entry; undefined for products not yet migrated. */
export const getProductEntry = (id: SaaProductId): ProductRegistryEntry | undefined =>
  PRODUCT_REGISTRY[id];

/**
 * Property products that collect a separate risk address (insured-property
 * address). Source of truth until these products gain a full registry entry,
 * at which point their entry's requiresRiskAddress flag takes over. Future
 * property products opt in by adding themselves here.
 */
export const RISK_ADDRESS_PRODUCTS: ReadonlySet<SaaProductId> = new Set<SaaProductId>([
  'woonhuis',
  'inboedel',
]);

/** Whether a product collects a separate risk address. */
export const requiresRiskAddress = (id: SaaProductId): boolean =>
  PRODUCT_REGISTRY[id]?.requiresRiskAddress ?? RISK_ADDRESS_PRODUCTS.has(id);
